/**
 * A field → value map, and the three questions the column rules ask of one: does a field carry a
 * value, can it be rendered at all, and what does a typed number clamp to.
 *
 * ── Ported from `move-with-me-frontend/.../shared/measurement/measurementValues.ts` ───────────
 * Reduced to what an AUTHORING surface needs. What is deliberately left upstream, so the omission
 * reads as scheduled rather than missed:
 *
 * · **`readActualValues` / `toWireActuals` and their `WireActualColumns` types are not here.** They
 *   serve mobile's in-gym LOGGER — what the athlete recorded — and this app has no logger. The
 *   PRESCRIBED half is not ported either, because this app already has one: `draftToRequest`
 *   assembles `PostSetInput` directly, field by field, and a second mapper over the same columns
 *   would be a second place for them to disagree.
 * · **`stepMeasurementValue`** — the ± stepper. No control in the set table steps a value; the
 *   cells are typed into. It comes back with the first stepper.
 *
 * What IS here is the part both column rules stand on, and `hasWireColumn` is the load-bearing one:
 * speed and tempo are first-class dictionary fields the SET has no numeric cell for, so offering
 * either as a column would open a cell whose value goes nowhere.
 *
 * No React, no DOM, no query hook, no i18next.
 */

import { measurementFieldDefinition, sortMeasurementFields } from './fieldDictionary';
import { ALL_MEASUREMENT_FIELD_KEYS, type MeasurementFieldKey } from './measurementField';

/**
 * A field → value map. A field is PRESENT or it is not; a present `0` is a real measurement —
 * RIR 0 is "to failure", 0 kg is a bodyweight movement — never a stand-in for empty.
 */
export type MeasurementValues = Partial<Record<MeasurementFieldKey, number>>;

/** A read source whose values may still be `null`. Every generated DTO satisfies this structurally. */
type NullableValues = Partial<Record<MeasurementFieldKey, number | null | undefined>>;

/**
 * Whether the SET has a numeric cell for this field.
 *
 * Two fields answer no, for different reasons: `speed` has no `prescribedSpeed` column on any set
 * grid this app draws, and `tempo` is a free-text notation rather than a number. Both remain full
 * dictionary entries — an exercise genuinely measures them — and both come back into every column
 * rule the moment there is a cell for them to land in.
 *
 * The authority is deliberately here rather than in either column rule, because BOTH end on it.
 */
const HAS_SET_CELL: Record<MeasurementFieldKey, boolean> = {
  weight: true,
  reps: true,
  duration: true,
  distance: true,
  rounds: true,
  rpe: true,
  rir: true,
  speed: false,
  tempo: false,
};

/** True when the wire — and the grid — has a numeric cell for this field. */
export function hasWireColumn(field: MeasurementFieldKey): boolean {
  return HAS_SET_CELL[field];
}

/**
 * THIS APP'S column order, which is the dictionary's with REPS ahead of WEIGHT.
 *
 * The 02.08 KANON gallery draws every set table as `# · TYP · POWT. · CIĘŻAR · RPE · PRZERWA`, and
 * a trainer authoring a prescription reads it in that order: how many, then how heavy. The vendored
 * contract's own `order` puts `Weight` first, and that file is a BYTE COPY of the backend's — it is
 * a cross-client fixture and is not edited to suit a web layout.
 *
 * So the swap lives here, in the render gate, and nowhere else. `sortMeasurementFields` — which
 * `measurementFieldsFromWire` ends on, and which every wire mapping therefore inherits — is
 * untouched, so nothing about what this app SENDS moves.
 *
 * **The consequence, stated rather than discovered later: the mobile app now displays the other
 * order.** Its own `builderColumns` sorts on the contract directly, so the same exercise reads
 * "CIĘŻAR · POWT." on the phone and "POWT. · CIĘŻAR" here. That is a real divergence between two
 * clients of one dictionary, taken deliberately on the gallery's authority; if it is to be closed,
 * it closes by moving `Weight` behind `Reps` in the backend's contract and re-vendoring, which
 * moves both surfaces at once and deletes this function's exception.
 */
const SWAPPED: Partial<Record<MeasurementFieldKey, MeasurementFieldKey>> = {
  weight: 'reps',
  reps: 'weight',
};

/** The dictionary's order with the one swap applied, built once. */
const DISPLAY_INDEX: ReadonlyMap<MeasurementFieldKey, number> = new Map(
  sortMeasurementFields([...ALL_MEASUREMENT_FIELD_KEYS])
    .map((field) => SWAPPED[field] ?? field)
    .map((field, index) => [field, index] as const),
);

/**
 * The fields that can actually be RENDERED as a column, in this app's display order — the gate
 * every column rule ends on, so no surface can open a cell whose value goes nowhere, and no surface
 * applies an order of its own.
 */
export function renderableColumns(fields: Iterable<MeasurementFieldKey>): MeasurementFieldKey[] {
  return [...fields]
    .filter(hasWireColumn)
    .sort((a, b) => (DISPLAY_INDEX.get(a) ?? 0) - (DISPLAY_INDEX.get(b) ?? 0));
}

/**
 * A field "has a value" when it is PRESENT, never when it is "non-zero".
 *
 * The distinction is the whole point and it has already cost once on the phone: a gate demanding a
 * value greater than zero refused RIR 0 ("to failure") and 0 kg (bodyweight), while the backend has
 * only ever required non-null.
 */
export function hasValue(values: NullableValues, field: MeasurementFieldKey): boolean {
  return values[field] != null;
}

/**
 * True when the field's values are whole numbers, read off THE DICTIONARY'S STEP: a field that
 * moves in whole units holds whole values.
 *
 * Not a field list and not a value type, so it stays right as the dictionary changes — RIR steps by
 * 1 and is whole, RPE steps by 0.5 and is not, and neither is stated twice.
 *
 * Load-bearing rather than cosmetic. `Rir` is `int?` on the backend while its value type is the
 * same `BoundedNumberWithStep` as the decimal `Rpe`, so a value-type rule lets a typed "2.5"
 * through — and a fractional int fails MODEL BINDING, which refuses the whole request before the
 * measurement contract gets to say anything.
 */
function isWholeNumberField(field: MeasurementFieldKey): boolean {
  const { step } = measurementFieldDefinition(field);
  return step != null && Number.isInteger(step);
}

/**
 * Clamp an entered value into the field's dictionary range and value type — the ONE place a typed
 * number is bounded, so nothing outside the contract can reach the payload.
 *
 * A field the dictionary gives no bounds (the text notation) is returned untouched: there is
 * nothing to clamp.
 */
export function clampMeasurementValue(field: MeasurementFieldKey, value: number): number {
  const { min, max } = measurementFieldDefinition(field);
  let next = value;
  if (min != null && next < min) next = min;
  if (max != null && next > max) next = max;
  return isWholeNumberField(field) ? Math.round(next) : next;
}
